import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Bot } from 'lucide-react';
import { useState } from 'react';
import { useLlmLogColumns } from './columns';
import { LlmLogDetailsDrawer } from './LlmLogDetailsDrawer';
import { LlmLogFilters } from './LlmLogFilters';

export const Route = createFileRoute('/admin-console/monitoring/llm-logs')({
    staticData: {
        permissions: ['admin_ai:read'],
        breadcrumb: {
            title: 'LLM logs',
        },
        ui: {
            icon: Bot,
            title: 'LLM logs',
            subtitle: 'View all LLM request logs across the platform',
            widthVariant: 'fullWidth',
            menuDescription: 'Review LLM API requests and responses',
        },
    },
    component: LlmLogsPage,
});

type LlmLogItem = RouterOutputs['admin']['llmRequestLog']['list']['rows'][number];
type QueryVariables = RouterInputs['admin']['llmRequestLog']['list'];

function LlmLogsPage() {
    const { trpc } = useTrpc();

    const [selectedItem, setSelectedItem] = useState<LlmLogItem | undefined>();
    const [filters, setFilters] = useState<Omit<QueryVariables, 'limit' | 'offset'>>({});

    const [queryVariables, setQueryVariables] = usePathState<QueryVariables>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
    });

    const { data, refetch, isFetching, error } = trpc.admin.llmRequestLog.list.useQuery({
        ...queryVariables,
        ...filters,
    });

    const columns = useLlmLogColumns(setSelectedItem);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'admin-llm-logs-table-columns',
            enableReordering: true,
            defaultHidden: ['correlationId', 'cachedTokens', 'userId'],
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <TableToolbar filters={<LlmLogFilters onChange={setFilters} />} variant="enhanced" />

                <LlmLogDetailsDrawer
                    item={selectedItem}
                    open={!!selectedItem}
                    onClose={() => setSelectedItem(undefined)}
                />

                <PrismaTable {...tableProps} />
            </TRPCErrorBoundary>
        </PageController>
    );
}
